import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CampaignProvider } from './context/CampaignContext';
import ProtectedRoute from './components/ProtectedRoute';

// ── Auth & plan pages ──────────────────────────────────────────────
import SignIn        from './pages/SignIn';
import SignUp        from './pages/SignUp';
import Upgrade       from './pages/Upgrade';
import PlanSelection from './pages/PlanSelection';
import ScrollToTop from './components/ScrollToTop';
import PaymentSuccess from './pages/PaymentSuccess';
import Admin         from './pages/Admin';

// ── App screens ────────────────────────────────────────────────────
import FreePlan      from './screens/screen0FreePlan';
import Onboarding    from './screens/screen1Onboarding';
import Dashboard     from './screens/screen2Dashboard';
import SetupWizard   from './screens/screen3SetupWizard';
import CampaignType  from './screens/screen4CampaignType';
import CampaignBuild from './screens/screen5CampaignBuilder';
import LaunchGuide   from './screens/screen6LaunchGuide';
import Results       from './screens/screen7Results';

// ── Confirm Email page ─────────────────────────────────────────────
function ConfirmEmail() {
  const navigate  = useNavigate();
  const { state } = useLocation();
  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(160deg,#f8faff,#eef2ff)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:'#fff', borderRadius:20, border:'1px solid #e2e8f0', padding:'40px 36px', width:'100%', maxWidth:440, textAlign:'center', boxShadow:'0 4px 40px rgba(99,102,241,0.08)' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>📧</div>
        <h2 style={{ fontSize:24, fontWeight:700, color:'#0f172a', marginBottom:12 }}>Check your email!</h2>
        <p style={{ fontSize:14, color:'#64748b', fontFamily:'sans-serif', lineHeight:1.65, marginBottom:28 }}>
          We sent a confirmation link to <strong>{state?.email || 'your email'}</strong>. Click the link to activate your account, then sign in.
        </p>
        <button
          style={{ width:'100%', padding:'14px', borderRadius:10, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', border:'none', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif' }}
          onClick={() => navigate('/signin')}
        >
          Go to Sign In →
        </button>
        <p style={{ fontSize:12, color:'#94a3b8', fontFamily:'sans-serif', marginTop:16 }}>
          Didn't receive it? Check your spam folder.
        </p>
      </div>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <CampaignProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>

            {/* ── Public routes ── */}
            <Route path="/"              element={<Navigate to="/signup" replace />} />
            <Route path="/signup"        element={<SignUp />} />
            <Route path="/signin"        element={<SignIn />} />
            <Route path="/confirm-email"    element={<ConfirmEmail />} />
            <Route path="/check-email"      element={<ConfirmEmail />} />
            <Route path="/payment-success"  element={<PaymentSuccess />} />
            <Route path="/admin"            element={<Admin />} />

            {/* ── Plan selection — shown once after signup ── */}
            <Route path="/plans" element={
              <ProtectedRoute><PlanSelection /></ProtectedRoute>
            }/>

            {/* ── Free plan routes (login required) ── */}
            <Route path="/onboarding" element={
              <ProtectedRoute><Onboarding /></ProtectedRoute>
            }/>
            <Route path="/free-setup" element={
              <ProtectedRoute><FreePlan /></ProtectedRoute>
            }/>
            <Route path="/dashboard" element={
              <ProtectedRoute><Dashboard /></ProtectedRoute>
            }/>

            {/* ── Paid routes (login + paid plan required) ── */}
            <Route path="/setup" element={
              <ProtectedRoute requirePaid><SetupWizard /></ProtectedRoute>
            }/>
            <Route path="/campaign-type" element={
              <ProtectedRoute requirePaid><CampaignType /></ProtectedRoute>
            }/>
            <Route path="/campaign-builder" element={
              <ProtectedRoute requirePaid><CampaignBuild /></ProtectedRoute>
            }/>
            <Route path="/launch-guide" element={
              <ProtectedRoute requirePaid><LaunchGuide /></ProtectedRoute>
            }/>
            <Route path="/results" element={
              <ProtectedRoute requirePaid><Results /></ProtectedRoute>
            }/>

            {/* ── Upgrade page (accessible anytime from dashboard) ── */}
            <Route path="/upgrade" element={<Upgrade />} />

            {/* ── Catch-all ── */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />

          </Routes>
        </BrowserRouter>
      </CampaignProvider>
    </AuthProvider>
  );
}